/**
 * lintLoopIntent — deterministic ambiguity lint for Loop Intent drafts.
 *
 * the Cascade's named-blocker taxonomy includes `interpretation_unclear`
 * (registry: catchball-governance) — a receiver who cannot tell what is being
 * asked. This lint helps the SENDER prevent that blocker before the ball is
 * thrown. Pure function, no LLM: the checklist renders even when AI is off
 * (Authority & Refusal Matrix §3 — "check the draft for ambiguity").
 *
 * Rules (v1):
 *  1. preliminary-marked requests with empty constraints or guardrails —
 *     maximum challengeability with no stated limits invites churn;
 *  2. asks with no question or deliverable phrasing — the receiver cannot
 *     tell what to return;
 *  3. constraints with no numbers or dates — a limit that cannot be checked;
 *  4. guardrails that duplicate constraints — the canon distinction
 *     (constraints block; guardrails guide) collapses.
 */

export interface LoopIntentLintInput {
  strategicContext?: string;
  asks: string[];
  constraints: string[];
  guardrails: string[];
  responseGuidance?: string;
  legitimacyMarking?: string | null;
}

export interface LoopIntentLintFinding {
  field: 'strategicContext' | 'asks' | 'constraints' | 'guardrails' | 'responseGuidance';
  message: string;
}

/** Question mark, interrogatives, or deliverable verbs — "what to return" phrasing. */
const ASK_PHRASING = new RegExp(
  [
    '\\?',
    '\\b(?:what|which|how|when|where|who|why|should|could|can we|do we)\\b',
    '\\b(?:propos\\w*|deliver\\w*|provide|produce|return|submit|rank\\w*|prioriti[sz]e\\w*|list|identify|recommend\\w*|assess\\w*|evaluat\\w*|estimat\\w*|draft|map|compare|sequence|scope)\\b',
  ].join('|'),
  'i',
);

/** Digits, or date-ish words (month names, quarters, halves, relative periods). */
const MEASURABLE = new RegExp(
  [
    '\\d',
    '\\b(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\\b',
    '\\b(?:q[1-4]|h[12]|eoy|eom|year-end|month-end|quarter-end)\\b',
  ].join('|'),
  'i',
);

function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function nonEmpty(entries: string[]): string[] {
  return entries.map((entry) => entry.trim()).filter(Boolean);
}

export function lintLoopIntent(input: LoopIntentLintInput): LoopIntentLintFinding[] {
  const findings: LoopIntentLintFinding[] = [];
  const asks = nonEmpty(input.asks);
  const constraints = nonEmpty(input.constraints);
  const guardrails = nonEmpty(input.guardrails);

  // Rule 1 — preliminary marking with no stated limits.
  if (input.legitimacyMarking === 'preliminary') {
    if (constraints.length === 0) {
      findings.push({
        field: 'constraints',
        message:
          'Marked preliminary with no constraints — the receiving tier has maximum latitude to challenge and no hard limits to work within. State budget/time/headcount limits, or expect an interpretation_unclear round.',
      });
    }
    if (guardrails.length === 0) {
      findings.push({
        field: 'guardrails',
        message:
          'Marked preliminary with no guardrails — name the lines that must not be crossed so early challenge stays inside them.',
      });
    }
  }

  // Rule 2 — asks with no question or deliverable phrasing.
  for (const ask of asks) {
    if (!ASK_PHRASING.test(ask)) {
      findings.push({
        field: 'asks',
        message: `Ask "${ask.length > 60 ? `${ask.slice(0, 57)}…` : ask}" names no question or deliverable — the receiver cannot tell what to return. Phrase it as a question or a "propose/deliver/rank…" instruction.`,
      });
    }
  }

  // Rule 3 — constraints without a number or date.
  for (const constraint of constraints) {
    if (!MEASURABLE.test(constraint)) {
      findings.push({
        field: 'constraints',
        message: `Constraint "${constraint.length > 60 ? `${constraint.slice(0, 57)}…` : constraint}" has no number or date — a limit that cannot be checked cannot block anything. Quantify it, or move it to guardrails.`,
      });
    }
  }

  // Rule 4 — guardrails duplicating constraints.
  const constraintSet = new Set(constraints.map(normalize));
  for (const guardrail of guardrails) {
    if (constraintSet.has(normalize(guardrail))) {
      findings.push({
        field: 'guardrails',
        message: `Guardrail "${guardrail.length > 60 ? `${guardrail.slice(0, 57)}…` : guardrail}" duplicates a constraint — constraints block, guardrails guide. Keep it in one place.`,
      });
    }
  }

  return findings;
}
